import { useSyncExternalStore } from 'react';
import {
  emptyMetaCache,
  metaCachePut,
  readMetaCache,
  touchMetaCache,
  VIDEO_META_VERSION,
  type CachedVideoMeta,
  type VideoMetaCache,
} from '@visious/core';

/**
 * R14's storage half for **videos** — the twenty lines core is not allowed to contain, second instance.
 *
 * Deliberately a near-copy of `channelMetaStore.ts` rather than an abstraction over it. What the two
 * share is already shared: the policy (LRU on last use, hard cap, staleness TTL, wholesale eviction) is
 * one implementation in `cache/metaCache.ts`, and 3d added no rule to it. What is *not* shared is a
 * storage key, a schema version and a `useSyncExternalStore` subscription, and a generic factory over
 * three constants would make one bump of `VIDEO_META_VERSION` evict channel avatars too.
 *
 * **The key prefix is deliberate**, for `channelMetaStore`'s two reasons: not `visious.token.*`, because
 * R9's `clearOtherInstances()` deletes every key under that prefix that is not the current instance's on
 * every boot; and **not per instance**, because what is cached is a title, an author and a duration —
 * facts about a YouTube video, identical on every Invidious deployment. Re-keying per origin would throw
 * away a whole history's worth of metadata the moment the reader switched instances, and re-fetch it at
 * 89–262 KB a row.
 *
 * Nothing here is account-specific either — these are public video records — so unlike the query cache
 * (`AUTH_QUERY_KEY`) it is not dropped on sign-out. That needs stating rather than assuming, because a
 * *history* is account-specific and this cache is populated by reading one: what it holds is "what is
 * video X called", never "X is in somebody's history". The ids in it are the ones this browser has looked
 * at, which is what a browser's own history already records in more detail.
 */

const STORAGE_KEY = 'visious.videoMeta';

/** Read once and kept in memory: this is on the render path and `localStorage` reads are synchronous. */
let cache: VideoMetaCache = load();
const listeners = new Set<() => void>();

function load(): VideoMetaCache {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (raw === null || raw === undefined) return emptyMetaCache<CachedVideoMeta>(VIDEO_META_VERSION);
    // R4's division of labour: `JSON.parse` happens out here, core validates the parsed value.
    return readMetaCache<CachedVideoMeta>(JSON.parse(raw) as VideoMetaCache | null, VIDEO_META_VERSION);
  } catch {
    // A blocked storage context, a quota error, or a blob some other version wrote. All three mean
    // "start empty", which for a cache costs one round of requests and nothing else.
    return emptyMetaCache<CachedVideoMeta>(VIDEO_META_VERSION);
  }
}

function persist(next: VideoMetaCache): void {
  cache = next;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Swallowed, and the in-memory value kept: a cache that does not survive a reload is still a cache
    // for the rest of this session. The quota case is more reachable here than for channels — 1000
    // entries is ~250 KB against `localStorage`'s few megabytes — and losing the write is the right
    // outcome, since core's cap has already bounded what we are trying to store.
  }
  for (const listener of listeners) listener();
}

export function putVideoMeta(metas: readonly CachedVideoMeta[]): void {
  const next = metaCachePut(cache, metas);
  if (next !== cache) persist(next);
}

/** Record that these rows were rendered from the cache, which is what keeps the LRU order meaningful. */
export function touchVideoMeta(ids: readonly string[], nowEpochSec: number): void {
  const next = touchMetaCache(cache, ids, nowEpochSec);
  // `touchMetaCache` returns the same object when nothing moved, so a list re-rendering in the same
  // second writes nothing — which matters, because this runs on every render of every history row.
  if (next !== cache) persist(next);
}

/** How many rows are held — the number the settings page puts next to "clear" (3e). */
export function videoMetaCount(): number {
  return cache.entries.length;
}

/**
 * Throw the cache away. §8's Settings page owns this, and it is a *privacy* control rather than a
 * maintenance one: the ids in here are the videos this browser has looked at, so "forget them" has to be
 * a thing a reader can do without clearing every site's storage.
 *
 * Deliberately **not** wired to sign-out — see the note at the top of this file for why what is cached
 * is not account-specific.
 */
export function clearVideoMeta(): void {
  persist(emptyMetaCache<CachedVideoMeta>(VIDEO_META_VERSION));
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

function getSnapshot(): VideoMetaCache {
  return cache;
}

export function useVideoMetaCache(): VideoMetaCache {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
